import { defineStore } from "pinia"
import { fetchNotifications, markNotificationsRead, SEVERITY_ORDER } from "@/services/notifications"
import { logger } from "@/services/logger"
import { backgroundPoll, mayRequestInBackground } from "@/services/backgroundRequests"

// Matches the backend's snapshot TTL (lewara.notifications.cache-ttl), so a poll is normally a
// cache hit server-side and the monitors run about once per interval per tenant.
const POLL_INTERVAL_MS = 5 * 60 * 1000

const poll = backgroundPoll(() => useNotificationCenterStore().load({ background: true }), POLL_INTERVAL_MS)

const emptyCounts = () => ({ info: 0, warning: 0, critical: 0, total: 0, unread: 0 })

/**
 * The proactive notification centre — the monitored alerts (unbilled services, overdue reports,
 * looming losses) the backend derives from live data.
 *
 * Deliberately separate from `store/notification.js`, which is the transient error-snackbar store
 * `services/api.js` writes to from its response interceptor.
 */
export const useNotificationCenterStore = defineStore("notificationCenter", {
  state: () => ({
    notifications: [],
    counts: emptyCounts(),
    evaluatedAt: null,
    loading: false,
    // True once a load resolved at least once — lets the bell tell "nothing yet" from "nothing".
    loaded: false,
  }),
  getters: {
    unreadCount: state => state.counts.unread,
    hasUnread: state => state.counts.unread > 0,
    /**
     * The most urgent level among the *unread* notifications — what the count badge is coloured by,
     * so its colour matches the number it shows rather than the list as a whole.
     */
    highestUnreadSeverity: state => {
      const unread = state.notifications.filter(notification => !notification.read)
      return SEVERITY_ORDER.find(severity => unread.some(n => n.severity === severity)) ?? null
    },
  },
  actions: {
    /** See services/backgroundRequests.js — shared with the release notes. */
    canPoll() {
      return mayRequestInBackground()
    },

    applyResponse(data) {
      this.notifications = data?.notifications ?? []
      this.counts = { ...emptyCounts(), ...(data?.counts ?? {}) }
      this.evaluatedAt = data?.evaluatedAt ?? null
      this.loaded = true
    },

    /**
     * [background] for the poll: a failure with no response is then neither shown nor reported, see
     * `isServerUnreachable` in services/api.js. Opening the page and saving the settings load in the
     * foreground.
     */
    async load({ background = false } = {}) {
      if (!this.canPoll()) return
      this.loading = true
      try {
        const response = await fetchNotifications({ background })
        this.applyResponse(response.data)
      } catch (error) {
        // A failing bell must never break the page it sits on; the interceptor already surfaces
        // server errors, so keep the last known list instead of blanking it.
        logger.error(`Failed to load notifications: ${error}`)
      } finally {
        this.loading = false
      }
    },

    /**
     * Acknowledges [keys]; an empty list marks everything currently visible as read.
     *
     * The local state flips first so the row and the counter react to the click straight away
     * instead of after the round-trip; the response is authoritative and replaces the list, and a
     * failure resyncs from the server so an optimistic flip can never stick.
     */
    async markRead(keys = []) {
      if (!this.canPoll()) return

      const acknowledged = keys.length ? new Set(keys) : null
      const previous = this.notifications
      this.notifications = this.notifications.map(notification =>
        !acknowledged || acknowledged.has(notification.key) ? { ...notification, read: true } : notification
      )
      this.counts = { ...this.counts, unread: this.notifications.filter(n => !n.read).length }

      try {
        const response = await markNotificationsRead(keys)
        this.applyResponse(response.data)
      } catch (error) {
        logger.error(`Failed to mark notifications as read: ${error}`)
        this.notifications = previous
        await this.load()
      }
    },

    markAllRead() {
      return this.markRead([])
    },

    reset() {
      this.notifications = []
      this.counts = emptyCounts()
      this.evaluatedAt = null
      this.loaded = false
    },

    /** The bell polls while it is mounted; see `backgroundPoll`. */
    startPolling() {
      poll.start()
    },

    stopPolling() {
      poll.stop()
    },
  },
})
